/**
 * 邮箱验证页：6 位验证码分格输入 + 60 秒倒计时重发。
 *
 * 后端接口（见 auth.routes.ts）：
 *   POST /auth/parent/register/confirm  { email, code }
 *   POST /auth/parent/register/resend   { email }
 * 验证通过后**不自动登录**（已确认的裁定），跳登录页。
 */
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ERROR_CODES } from '@fht/shared';
import * as authApi from '../../lib/api/auth';
import { ApiError, toErrorMessage } from '../../lib/api/error';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { BuildStamp } from '../../components/layout/BuildStamp';
import { formatCountdown } from '../../lib/utils';

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [digits, setDigits] = useState<string[]>(Array.from({ length: CODE_LENGTH }, () => ''));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_SECONDS);

  // 倒计时：重发按钮 60 秒冷却（与后端 EMAIL_CODE_COOLDOWN 一致）
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = globalThis.setInterval(() => {
      setCountdown((value) => (value <= 1 ? 0 : value - 1));
    }, 1000);
    return () => globalThis.clearInterval(timer);
  }, [countdown]);

  const code = digits.join('');

  const setDigit = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '');
    setDigits((prev) => {
      const next = [...prev];
      if (cleaned.length > 1) {
        // 粘贴多个数字：从当前格开始铺开
        for (let i = 0; i < cleaned.length && index + i < CODE_LENGTH; i++) {
          next[index + i] = cleaned[i] ?? '';
        }
      } else {
        next[index] = cleaned;
      }
      return next;
    });
    const advance = index + Math.max(1, cleaned.length);
    if (cleaned && advance < CODE_LENGTH) inputsRef.current[advance]?.focus();
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!text) return;
    event.preventDefault();
    setDigits(Array.from({ length: CODE_LENGTH }, (_, i) => text[i] ?? ''));
    inputsRef.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    if (!email.trim()) {
      setError('请填写注册时使用的邮箱');
      return;
    }
    if (code.length !== CODE_LENGTH) {
      setError('请输入 6 位验证码');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.confirmRegisterEmail(email.trim(), code);
      navigate('/login', { replace: true, state: { verified: true } });
    } catch (err) {
      setError(toErrorMessage(err));
      // 验证码错误就清空重填，避免用户反复提交同一个错码
      if (err instanceof ApiError && err.code === ERROR_CODES.TOKEN_INVALID) {
        setDigits(Array.from({ length: CODE_LENGTH }, () => ''));
        inputsRef.current[0]?.focus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('请先填写邮箱');
      return;
    }
    setResending(true);
    try {
      await authApi.resendRegisterEmail(email.trim());
      setInfo('验证码已重新发送，请查收邮件（若邮件功能未开启，请联系管理员从服务器日志获取）');
      setCountdown(RESEND_SECONDS);
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span aria-hidden="true" className="text-4xl">
            📧
          </span>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">验证邮箱</h1>
          <p className="mt-1 text-sm text-slate-500">
            我们发送了一封 6 位验证码邮件，验证后即可登录
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <Input
            label="邮箱"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <div className="space-y-2">
            <span className="block text-sm font-medium text-slate-700">6 位验证码</span>
            <div className="flex justify-between gap-2">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputsRef.current[index] = el;
                  }}
                  value={digit}
                  onChange={(e) => setDigit(index, e.target.value)}
                  onKeyDown={(e) => onKeyDown(index, e)}
                  onPaste={onPaste}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  aria-label={`验证码第 ${index + 1} 位`}
                  className="h-14 w-full rounded-xl border border-slate-300 text-center text-2xl font-semibold text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              ))}
            </div>
          </div>

          {error && (
            <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
              {error}
            </div>
          )}
          {info && (
            <div role="status" className="rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
              {info}
            </div>
          )}

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            完成验证
          </Button>

          <div className="flex items-center justify-between text-sm">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void onResend()}
              loading={resending}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `重新发送（${formatCountdown(countdown)}）` : '重新发送验证码'}
            </Button>
            <Link to="/login" className="font-medium text-brand-600 hover:underline">
              返回登录
            </Link>
          </div>
        </form>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          位置与其它 4 个未登录页一致：居中卡片（这里是表单本身）**下方的页脚处居中**。
          复用全站唯一的 `BuildStamp`（见 lib/buildStamp.ts）——值同源、格式同形。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}
